import React, { useState, useEffect, useCallback } from 'react';
import liff from '@line/liff';
import { buildApiUrl } from '../../utils/apiBase';
import { supabase } from '../../supabaseClient';
import ExpenseModal from '../expense/ExpenseModal';
import SelectorModal from '../SelectorModal';
import TripListView from './TripListView';
import TripDetailView from './TripDetailView';
import TripExpenseSummary from '../expense/TripExpenseSummary';

const TRIP_ICONS = ['✈️', '🏖', '🏔', '🏙', '🏯', '🎡', '🎢', '🚂', '🚗', '🛳', '⛺', '🎒', '🛍', '🍜', '🍻', '🎉', '🇯🇵', '🇰🇷'];

const TripManager = ({ appUser, userProfile, onBack, onShowToast }) => {
  const [mode, setMode] = useState('list'); // 'list', 'detail', 'summary'
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [listViewMode, setListViewMode] = useState('grid'); // 'table' | 'grid'
  
  // Trip Detail State
  const [tripExpenses, setTripExpenses] = useState([]);
  const [tripMembers, setTripMembers] = useState([]);
  const [friends, setFriends] = useState([]);
  
  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newTripName, setNewTripName] = useState('');
  const [newTripCurrency, setNewTripCurrency] = useState('THB');
  const [newTripIcon, setNewTripIcon] = useState('✈️');
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [isEditTripModalOpen, setIsEditTripModalOpen] = useState(false);
  const [editTripName, setEditTripName] = useState('');
  const [editTripIcon, setEditTripIcon] = useState('✈️');
  
  // Expense Modal State (Reuse)
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteConfig, setDeleteConfig] = useState(null); // { type: 'trip' | 'expense', data: any }

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    try {
      const accessToken = liff?.getAccessToken?.();
      if (!accessToken) throw new Error('ไม่พบ LINE access token');

      const res = await fetch(buildApiUrl('/api/trips'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken, action: 'list' }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'โหลดทริปไม่สำเร็จ');
      }

      const json = await res.json();
      setTrips(json.trips || []);
    } catch (error) {
      console.error('Error fetching trips:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFriends = useCallback(async () => {
    try {
        const { data, error } = await supabase
            .from('friendships')
            .select(`
                user_id_1,
                user_id_2,
                user1:users!user_id_1(id, display_name, picture_url, username),
                user2:users!user_id_2(id, display_name, picture_url, username)
            `)
            .or(`user_id_1.eq.${appUser.id},user_id_2.eq.${appUser.id}`)
            .eq('status', 'accepted');

        if (error) throw error;
        const friendList = data.map(f => f.user_id_1 === appUser.id ? f.user2 : f.user1);
        setFriends(friendList);
    } catch (error) {
        console.error('Error fetching friends:', error);
    }
  }, [appUser]);

  const fetchPaymentMethods = useCallback(async (tripId) => {
      // บัตรของฉัน + บัตรแชร์ให้ฉัน + บัตรที่แชร์ในทริปนี้
      try {
        const ownerId = appUser?.id;
        const ownerIdStr = ownerId != null ? String(ownerId) : null;
        const legacyLineId = userProfile?.userId;

        const baseOr = ['and(owner_id.is.null,user_id.is.null)'];
        if (ownerId != null) baseOr.push(`owner_id.eq.${ownerId}`);
        if (ownerIdStr) baseOr.push(`user_id.eq.${ownerIdStr}`);
        if (legacyLineId) baseOr.push(`user_id.eq.${legacyLineId}`);

        const { data: baseMethods } = await supabase
          .from('payment_methods')
          .select('*')
          .or(baseOr.join(','));

        let sharedMethods = [];
        let sharedIds = new Set();
        let shareMap = new Map();
        if (ownerId != null) {
          const accessToken = liff?.getAccessToken?.();
          if (accessToken) {
            const res = await fetch(buildApiUrl('/api/payment-method-shares-map'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                accessToken,
                ownerMethodIds: baseMethods.map(pm => pm.id)
              })
            });
            if (res.ok) {
              const json = await res.json();
              const sharedMethodIds = (json.sharedMethodIds || []).map(String);
              sharedIds = new Set(sharedMethodIds);
              const shareMapObj = json.shareMap || {};
              shareMap = new Map(Object.entries(shareMapObj));
              if (sharedMethodIds.length > 0) {
                const { data: sharedData } = await supabase
                  .from('payment_methods')
                  .select('*')
                  .in('id', sharedMethodIds);
                sharedMethods = sharedData || [];
              }
            }
          }
        }

        let tripMethods = [];
        let tripIds = new Set();
        if (tripId) {
          const accessToken = liff?.getAccessToken?.();
          if (accessToken) {
            const tripRes = await fetch(buildApiUrl('/api/trip-payment-methods'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ accessToken, action: 'list', tripId }),
            });
            if (tripRes.ok) {
              const tripJson = await tripRes.json();
              const tripRows = tripJson.rows || [];
              tripMethods = tripRows.map(r => r.payment_methods).filter(Boolean);
              tripIds = new Set(tripRows.map(r => r.payment_method_id));
            }
          }
        }

        const methodsMap = new Map();
        const flagsMap = new Map();
        const upsert = (pm, flags = {}) => {
          if (!pm || !pm.id) return;
          const existing = methodsMap.get(pm.id) || pm;
          methodsMap.set(pm.id, existing);
          flagsMap.set(pm.id, { ...(flagsMap.get(pm.id) || {}), ...flags });
        };

        (baseMethods || []).forEach(pm => {
          const isOwner = ownerId != null
            ? pm.owner_id === ownerId || (pm.user_id && (pm.user_id === legacyLineId || pm.user_id === ownerIdStr))
            : pm.user_id === legacyLineId;
          const isSystem = !pm.owner_id && !pm.user_id;
          upsert(pm, { isOwner, isSystem });
        });

        sharedMethods.forEach(pm => upsert(pm, { isShared: true }));
        tripMethods.forEach(pm => upsert(pm, { isTrip: true }));

        const normalizedShareMap = new Map();
        shareMap.forEach((ids, key) => {
          normalizedShareMap.set(String(key), (ids || []).map(String));
        });

        const friendIdToName = new Map(friends.map(f => [f.id, f.display_name]));
        const friendLineToName = new Map(friends.map(f => [f.line_user_id, f.display_name]));

        const merged = Array.from(methodsMap.values()).map(pm => {
          const flags = flagsMap.get(pm.id) || {};
          const access = flags.isSystem
            ? 'system'
            : flags.isOwner
              ? 'owner'
              : flags.isShared || sharedIds.has(pm.id)
                ? 'shared'
                : flags.isTrip || tripIds.has(pm.id)
                  ? 'trip'
                  : 'shared';
          const ownerName = pm.owner_id
            ? friendIdToName.get(pm.owner_id)
            : pm.user_id
              ? friendLineToName.get(pm.user_id)
              : null;
          return {
            ...pm,
            access,
            owner_name: ownerName,
            shared_in_trip: tripIds.has(pm.id),
            shared_to_ids: normalizedShareMap.get(String(pm.id)) || []
          };
        });

        setPaymentMethods(merged || []);
      } catch (e) {}
  }, [userProfile, appUser, friends]);

  const fetchTripDetails = useCallback(async (tripId) => {
    setLoading(true);
    try {
        // 1. Fetch Expenses
        const { data: expenses, error: expError } = await supabase
            .from('expenses')
            .select('*, debts(status)')
            .eq('trip_id', tripId)
            .order('occurred_at', { ascending: false });
        
        if (expError) throw expError;
        setTripExpenses(expenses);

        // 2. Fetch Members
        const accessToken = liff?.getAccessToken?.();
        if (!accessToken) throw new Error('ไม่พบ LINE access token');

        const membersRes = await fetch(buildApiUrl('/api/trip-members'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accessToken, action: 'list', tripId })
        });
        if (!membersRes.ok) {
            const text = await membersRes.text();
            throw new Error(text || 'โหลดสมาชิกไม่สำเร็จ');
        }
        const membersJson = await membersRes.json();
        setTripMembers(membersJson.members || []);

    } catch (error) {
        console.error('Error fetching trip details:', error);
        onShowToast('โหลดข้อมูลทริปไม่สำเร็จ', 'error');
    } finally {
        setLoading(false);
    }
  }, [onShowToast]);

  useEffect(() => {
    if (appUser) {
      fetchTrips();
      fetchFriends();
    }
  }, [appUser, fetchTrips, fetchFriends]);

  useEffect(() => {
    if (selectedTrip) {
      fetchTripDetails(selectedTrip.id);
      fetchPaymentMethods(selectedTrip.id);
    } else {
      setPaymentMethods([]);
    }
  }, [selectedTrip, fetchTripDetails, fetchPaymentMethods]);

  const handleDeleteTrip = (e, trip) => {
      e.stopPropagation();
      setDeleteConfig({ type: 'trip', data: trip });
      setIsDeleteModalOpen(true);
  };

  const handleRemoveMemberClick = (member) => {
      const isCreator = selectedTrip.created_by === appUser.id;
      const isSelf = member.id === appUser.id;

      if (!isCreator && !isSelf) return; // คนอื่นลบไม่ได้ นอกจากเจ้าของทริป หรือเจ้าตัวกดออกเอง
      if (isCreator && isSelf) return; // เจ้าของทริปออกจากทริปไม่ได้ (ต้องลบทริป)

      setDeleteConfig({ type: 'member', data: member });
      setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
      if (!deleteConfig) return;
      setLoading(true);
      try {
          if (deleteConfig.type === 'trip') {
              const accessToken = liff?.getAccessToken?.();
              if (!accessToken) throw new Error('ไม่พบ LINE access token');

              const res = await fetch(buildApiUrl('/api/trips'), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                      accessToken,
                      action: 'delete',
                      tripId: deleteConfig.data.id
                  })
              });
              if (!res.ok) {
                  const text = await res.text();
                  throw new Error(text || 'ลบทริปไม่สำเร็จ');
              }
              onShowToast('ลบทริปเรียบร้อย');
              fetchTrips();
          } else if (deleteConfig.type === 'expense') {
              const accessToken = liff?.getAccessToken?.();
              if (!accessToken) throw new Error('ไม่พบ LINE access token');

              const res = await fetch(buildApiUrl('/api/expenses'), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                      accessToken,
                      action: 'delete',
                      expenseId: deleteConfig.data.id
                  })
              });
              if (!res.ok) {
                  const text = await res.text();
                  throw new Error(text || 'ลบรายการไม่สำเร็จ');
              }
              onShowToast('ลบรายการแล้ว');
              setIsExpenseModalOpen(false);
              setEditingExpense(null);
              fetchTripDetails(selectedTrip.id);
          } else if (deleteConfig.type === 'member') {
              const member = deleteConfig.data;
              const isSelf = member.id === appUser.id;
              
              const accessToken = liff?.getAccessToken?.();
              if (!accessToken) throw new Error('ไม่พบ LINE access token');

              const res = await fetch(buildApiUrl('/api/trip-members'), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                      accessToken,
                      action: 'remove',
                      tripId: selectedTrip.id,
                      memberId: member.id
                  })
              });
              if (!res.ok) {
                  const text = await res.text();
                  throw new Error(text || 'ลบสมาชิกไม่สำเร็จ');
              }
              
              onShowToast(isSelf ? 'ออกจากทริปเรียบร้อย' : 'ลบสมาชิกเรียบร้อย');
              
              if (isSelf) {
                  setMode('list');
                  setSelectedTrip(null);
                  fetchTrips();
              } else {
                  fetchTripDetails(selectedTrip.id);
                  fetchTrips(); // อัปเดตข้อมูลหน้า List (เช่น Avatar สมาชิก) รอไว้เลย
              }
          }
          setIsDeleteModalOpen(false);
          setDeleteConfig(null);
      } catch (error) {
          console.error('Error deleting:', error);
          onShowToast('ลบไม่สำเร็จ', 'error');
      } finally {
          setLoading(false);
      }
  };

  const handleUpdateTrip = async () => {
      if (!editTripName.trim() || !selectedTrip) return;
      setLoading(true);
      try {
          const accessToken = liff?.getAccessToken?.();
          if (!accessToken) throw new Error('ไม่พบ LINE access token');

          const res = await fetch(buildApiUrl('/api/trips'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken,
              action: 'update',
              tripId: selectedTrip.id,
              name: editTripName.trim(),
              icon: editTripIcon
            })
          });
          if (!res.ok) {
            const text = await res.text();
            throw new Error(text || 'แก้ไขไม่สำเร็จ');
          }

          setSelectedTrip(prev => ({ ...prev, name: editTripName.trim(), icon: editTripIcon }));
          fetchTrips();
          setIsEditTripModalOpen(false);
          onShowToast('แก้ไขชื่อทริปเรียบร้อย');
      } catch (error) {
          console.error('Error updating trip:', error);
          onShowToast('แก้ไขไม่สำเร็จ', 'error');
      } finally {
          setLoading(false);
      }
  };

  const handleCreateTrip = async () => {
      if (!newTripName.trim()) return;
      if (!appUser?.id) {
          onShowToast('ยังไม่พบผู้ใช้ในระบบ กรุณาเข้าสู่ระบบใหม่', 'error');
          return;
      }
      setLoading(true);
      try {
          const accessToken = liff?.getAccessToken?.();
          if (!accessToken) throw new Error('ไม่พบ LINE access token');

          const res = await fetch(buildApiUrl('/api/trips'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                  accessToken,
                  action: 'create',
                  name: newTripName.trim(),
                  currency: newTripCurrency,
                  icon: newTripIcon
              })
          });
          if (!res.ok) {
              const text = await res.text();
              throw new Error(text || 'สร้างทริปไม่สำเร็จ');
          }

          setIsCreateModalOpen(false);
          setNewTripName('');
          setNewTripCurrency('THB');
          setNewTripIcon('✈️');
          fetchTrips();
          onShowToast('สร้างทริปเรียบร้อย');
      } catch (error) {
          console.error('Error creating trip:', error);
          onShowToast(`สร้างทริปไม่สำเร็จ: ${error.message || 'เกิดข้อผิดพลาด'}`, 'error');
      } finally {
          setLoading(false);
      }
  };

  const handleAddMember = async (friendName, friendObj) => {
      if (!friendObj) return;
      try {
          // Check duplicate
          if (tripMembers.some(m => m.id === friendObj.id)) {
              onShowToast('เพื่อนคนนี้อยู่ในทริปแล้ว', 'error');
              return;
          }

          const accessToken = liff?.getAccessToken?.();
          if (!accessToken) throw new Error('ไม่พบ LINE access token');

          const res = await fetch(buildApiUrl('/api/trip-members'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              accessToken,
              action: 'add',
              tripId: selectedTrip.id,
              memberId: friendObj.id
            })
          });
          if (!res.ok) {
            const text = await res.text();
            throw new Error(text || 'เพิ่มสมาชิกไม่สำเร็จ');
          }
          
          fetchTripDetails(selectedTrip.id);
          setIsAddMemberOpen(false);
          onShowToast(`เพิ่ม ${friendName} เข้าทริปแล้ว`);
      } catch (error) {
          onShowToast('เพิ่มสมาชิกไม่สำเร็จ', 'error');
      }
  };

  const handleSaveExpense = async (formData) => {
      setLoading(true);
      try {
        const buildMonthlyOccurrences = (baseDate, months) => {
          const dates = [];
          for (let i = 0; i < months; i += 1) {
            const next = new Date(baseDate);
            next.setMonth(baseDate.getMonth() + i);
            dates.push(next);
          }
          return dates;
        };

        let identifier = formData.identifier;
        let payerUserId = null;

        if (formData.payerMode === 'other') {
            if (formData.selectedFriend) {
                identifier = formData.selectedFriend.line_user_id || formData.selectedFriend.display_name;
                payerUserId = formData.selectedFriend.id;
            } else {
                identifier = formData.otherPayerName;
            }
        } else {
            identifier = appUser?.line_user_id || userProfile?.userId;
            payerUserId = appUser?.id;
        }

        const payload = { 
            user_id: userProfile?.userId,
            identifier: identifier,
            payer_user_id: payerUserId,
            amount: parseFloat(formData.amount),
            note: formData.note,
            currency: formData.currency,
            occurred_at: new Date(formData.occurredAt).toISOString(),
            payment_method_id: formData.paymentMethodId || null,
            payment_method: formData.paymentMethod,
            trip_id: formData.tripId // Important!
        };

        if (editingExpense) {
            const accessToken = liff?.getAccessToken?.();
            if (!accessToken) throw new Error('ไม่พบ LINE access token');

            const res = await fetch(buildApiUrl('/api/expenses'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    accessToken,
                    action: 'update',
                    expenseId: editingExpense.id,
                    payload
                })
            });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'แก้ไขไม่สำเร็จ');
            }

            onShowToast('แก้ไขรายการเรียบร้อย');
        } else {
            const shouldRepeat = formData.repeatType === 'monthly' && formData.repeatMonths > 1;
            const rows = shouldRepeat
              ? buildMonthlyOccurrences(new Date(formData.occurredAt), formData.repeatMonths).map((date) => ({
                  ...payload,
                  occurred_at: date.toISOString()
                }))
              : [payload];

            const accessToken = liff?.getAccessToken?.();
            if (!accessToken) throw new Error('ไม่พบ LINE access token');

            if (shouldRepeat) {
                for (const row of rows) {
                    const res = await fetch(buildApiUrl('/api/expenses'), {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            accessToken,
                            action: 'create',
                            payload: row
                        })
                    });
                    if (!res.ok) {
                        const text = await res.text();
                        throw new Error(text || 'บันทึกไม่สำเร็จ');
                    }
                }
            } else {
                const res = await fetch(buildApiUrl('/api/expenses'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        accessToken,
                        action: 'create',
                        payload: rows[0]
                    })
                });
                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(text || 'บันทึกไม่สำเร็จ');
                }
            }

            onShowToast('บันทึกรายจ่ายในทริปเรียบร้อย');
        }

        setIsExpenseModalOpen(false);
        setEditingExpense(null);
        fetchTripDetails(selectedTrip.id);
      } catch (error) {
          console.error(error);
          onShowToast('บันทึกไม่สำเร็จ', 'error');
      } finally {
          setLoading(false);
      }
  };

  const handleDeleteExpense = (id) => {
      setDeleteConfig({ type: 'expense', data: { id } });
      setIsDeleteModalOpen(true);
  };

  // Helper for display
  const getPayerDisplay = (expense) => {
    if (expense.payer_user_id === appUser?.id) return 'ฉัน';
    const member = tripMembers.find(m => m.id === expense.payer_user_id);
    if (member) return member.display_name;
    return expense.identifier;
  };
  const getPaymentStyle = () => ({ color: 'var(--color-primary)', icon: null }); // Simplified

  return (
    <div style={{ padding: '20px', paddingBottom: '100px', backgroundColor: 'var(--color-bg)', minHeight: '100vh', fontFamily: 'var(--font-body)' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {mode === 'summary' && selectedTrip ? (
          <TripExpenseSummary 
            trip={selectedTrip}
            expenses={tripExpenses}
            members={tripMembers}
            appUser={appUser}
            onBack={() => setMode('detail')}
            onShowToast={onShowToast}
            paymentMethods={paymentMethods}
          />
        ) : mode === 'detail' && selectedTrip ? (
            <TripDetailView 
                trip={selectedTrip}
                members={tripMembers}
                expenses={tripExpenses}
                userProfile={userProfile}
                appUser={appUser}
                listViewMode={listViewMode}
                onBack={() => { setMode('list'); setSelectedTrip(null); }}
                onEditTrip={() => { 
                    setEditTripName(selectedTrip.name); 
                    setEditTripIcon(selectedTrip.icon || '✈️'); 
                    setIsEditTripModalOpen(true); 
                }}
                onAddMember={() => setIsAddMemberOpen(true)}
                onRemoveMember={handleRemoveMemberClick}
                onSummary={() => setMode('summary')}
                onToggleViewMode={setListViewMode}
                onAddExpense={() => { setEditingExpense(null); setIsExpenseModalOpen(true); }}
                onEditExpense={(item) => { setEditingExpense(item); setIsExpenseModalOpen(true); }}
                onDeleteExpense={handleDeleteExpense}
                getPayerDisplay={getPayerDisplay}
                getPaymentStyle={getPaymentStyle}
            />
        ) : (
            <TripListView 
                trips={trips}
                loading={loading}
                appUser={appUser}
                onBack={onBack}
                onCreateTrip={() => setIsCreateModalOpen(true)}
                onSelectTrip={(trip) => { setSelectedTrip(trip); setMode('detail'); }}
                onDeleteTrip={handleDeleteTrip}
            />
        )}
      </div>

      {/* Modals */}
      {mode === 'list' && isCreateModalOpen && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                <div style={{ backgroundColor: 'var(--color-glass-strong)', border: '1px solid rgba(255, 255, 255, 0.8)', padding: '25px', borderRadius: '16px', width: '80%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(30, 70, 90, 0.16)' }}>
                    <h3 style={{ marginTop: 0, color: 'var(--color-text)' }}>ตั้งชื่อทริปใหม่</h3>
                    
                    <input type="text" value={newTripName} onChange={e => setNewTripName(e.target.value)} placeholder="เช่น เที่ยวเชียงใหม่, ปาร์ตี้ปีใหม่" style={{ width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '8px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-text)', boxSizing: 'border-box' }} autoFocus />
                    
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', color: 'var(--color-text-muted)' }}>สกุลเงินหลักของทริป</label>
                        <select value={newTripCurrency} onChange={e => setNewTripCurrency(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-text)' }}>
                            <option value="THB">THB (บาท)</option>
                            <option value="USD">USD (ดอลลาร์)</option>
                            <option value="JPY">JPY (เยน)</option>
                            <option value="KRW">KRW (วอน)</option>
                            <option value="EUR">EUR (ยูโร)</option>
                            <option value="GBP">GBP (ปอนด์)</option>
                            <option value="CNY">CNY (หยวน)</option>
                            <option value="SGD">SGD (สิงคโปร์ดอลลาร์)</option>
                        </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px', marginBottom: '15px' }}>
                        {TRIP_ICONS.map(icon => (
                            <button 
                                key={icon} 
                                onClick={() => setNewTripIcon(icon)}
                                style={{ fontSize: '20px', padding: '8px', border: newTripIcon === icon ? '2px solid var(--color-primary)' : '1px solid var(--color-border)', borderRadius: '8px', backgroundColor: newTripIcon === icon ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)', cursor: 'pointer' }}
                            >{icon}</button>
                        ))}
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button onClick={() => setIsCreateModalOpen(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-glass-strong)', color: 'var(--color-text-muted)' }}>ยกเลิก</button>
                        <button
                          onClick={handleCreateTrip}
                          disabled={!appUser?.id || loading}
                          style={{
                            flex: 1,
                            padding: '10px',
                            borderRadius: '8px',
                            border: 'none',
                            background: 'var(--color-primary)',
                            backgroundImage: 'var(--color-primary-gradient)',
                            color: '#fff',
                            fontWeight: 'bold',
                            cursor: !appUser?.id || loading ? 'not-allowed' : 'pointer',
                            opacity: !appUser?.id || loading ? 0.6 : 1
                          }}
                        >
                          สร้าง
                        </button>
                    </div>
                </div>
            </div>
      )}

      {mode === 'detail' && (
        <>
            <ExpenseModal 
                isOpen={isExpenseModalOpen}
                onClose={() => setIsExpenseModalOpen(false)}
                onSave={handleSaveExpense}
                editingExpense={editingExpense}
                userProfile={userProfile}
                savedPayers={[]} // Use friends/members instead
                paymentMethods={paymentMethods}
                onPaymentMethodsChange={() => fetchPaymentMethods(selectedTrip?.id)}
                loading={loading}
                onShowToast={onShowToast}
                friends={tripMembers} // Only show trip members in selector
                shareCandidates={friends}
                appUser={appUser}
                onDelete={handleDeleteExpense}
                tripId={selectedTrip?.id}
                defaultCurrency={selectedTrip?.currency}
            />

            {isAddMemberOpen && (
                <SelectorModal 
                    type="payer" // Reuse payer selector style
                    options={[]}
                    friends={friends.filter(f => !tripMembers.some(m => m.id === f.id))} // Filter out existing members
                    onSelect={(name, obj) => handleAddMember(name, obj)}
                    onClose={() => setIsAddMemberOpen(false)}
                />
            )}

            {isEditTripModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                    <div style={{ backgroundColor: 'var(--color-glass-strong)', border: '1px solid rgba(255, 255, 255, 0.8)', padding: '25px', borderRadius: '16px', width: '80%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(30, 70, 90, 0.16)' }}>
                        <h3 style={{ marginTop: 0, color: 'var(--color-text)' }}>แก้ไขชื่อทริป</h3>
                        
                        <input type="text" value={editTripName} onChange={e => setEditTripName(e.target.value)} placeholder="ชื่อทริป" style={{ width: '100%', padding: '10px', marginBottom: '20px', borderRadius: '8px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-text)', boxSizing: 'border-box' }} autoFocus />

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px', marginBottom: '15px' }}>
                            {TRIP_ICONS.map(icon => (
                                <button 
                                    key={icon} 
                                    onClick={() => setEditTripIcon(icon)}
                                    style={{ fontSize: '20px', padding: '8px', border: editTripIcon === icon ? '2px solid var(--color-primary)' : '1px solid var(--color-border)', borderRadius: '8px', backgroundColor: editTripIcon === icon ? 'var(--color-primary-soft)' : 'var(--color-glass-strong)', cursor: 'pointer' }}
                                >{icon}</button>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => setIsEditTripModalOpen(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-glass-strong)', color: 'var(--color-text-muted)' }}>ยกเลิก</button>
                            <button onClick={handleUpdateTrip} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: 'var(--color-primary)', backgroundImage: 'var(--color-primary-gradient)', color: '#fff', fontWeight: 'bold' }}>บันทึก</button>
                        </div>
                    </div>
                </div>
            )}
        </>
      )}

        {isDeleteModalOpen && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2200 }}>
                <div style={{ backgroundColor: 'var(--color-glass-strong)', border: '1px solid rgba(255, 255, 255, 0.8)', padding: '25px', borderRadius: '16px', width: '90%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(30, 70, 90, 0.16)', textAlign: 'center' }}>
                    <h3 style={{ margin: '0 0 15px 0', color: 'var(--color-text)' }}>ยืนยันการลบ</h3>
                    <p style={{ color: 'var(--color-text-muted)', marginBottom: '25px' }}>
                         {deleteConfig?.type === 'trip' ? `คุณต้องการลบทริป "${deleteConfig.data.name}" ใช่หรือไม่?` : 
                          deleteConfig?.type === 'member' ? (deleteConfig.data.id === appUser.id ? 'คุณต้องการออกจากทริปนี้ใช่หรือไม่?' : `คุณต้องการลบ ${deleteConfig.data.display_name} ออกจากทริปใช่หรือไม่?`) :
                          'คุณต้องการลบรายการนี้ใช่หรือไม่?'}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                        <button 
                            onClick={() => setIsDeleteModalOpen(false)}
                            style={{ padding: '10px 20px', backgroundColor: 'var(--color-glass-strong)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            ยกเลิก
                        </button>
                        <button 
                            onClick={confirmDelete}
                            style={{ padding: '10px 20px', backgroundColor: 'var(--color-danger)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 6px 14px rgba(224, 86, 75, 0.35)' }}
                        >
                            {loading ? 'กำลังลบ...' : 'ลบรายการ'}
                        </button>
                    </div>
                </div>
            </div>
        )}
    </div>
  );
};

export default TripManager;
